import React, { useCallback, useEffect, useMemo } from 'react';
import PageLayout from '../../components/PageLayout';
import _t from 'counterpart';
import { CButton, CCard, CCardBody, CCardHeader, CCardTitle, CCol, CLink, CRow } from '@coreui/react';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { getAffiliateTransactions } from '../../services/BackendService';
import { useQuery } from 'react-query';
import Error from '../../components/Error';
import PaginationTable, { ISorter } from '../../components/PaginationTable';
import { useHistory } from 'react-router';
import { formatCurrency, formatDateTime, shortenId } from '../../helpers';
import { ITransaction } from '../transactions/types';

const CommissionTransactionsPage = () => {
  const limit = 10;
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [withdrawalPage, setWithdrawalPage] = useQueryParam('withdrawal-page', NumberParam);
  const [orderBy = 'createdAt|DESC', setOrderBy] = useQueryParam('orderBy', StringParam);
  const offset = Number(page) > 0 ? Number(page) * limit - limit : 0;
  const withdrawalOffset = Number(withdrawalPage) > 0 ? Number(withdrawalPage) * limit - limit : 0;

  const commissionTransactionsQuery = useQuery(['commission-transactions', page, orderBy], () =>
    getAffiliateTransactions(limit, offset, orderBy!, 'COMMISSION_PAYOUT')
  );

  const withdrawalTransactionsQuery = useQuery(['withdrawal-transactions', withdrawalPage, orderBy], () =>
    getAffiliateTransactions(limit, withdrawalOffset, orderBy!, 'WITHDRAWAL_CORRECTION')
  );

  const history = useHistory();

  const handleDetailsClicked = useCallback(
    (id: string) => {
      history.push(`/transactions/${id}`);
    },
    [history]
  );

  useEffect(() => {
    setOrderBy('createdAt|DESC', 'replaceIn');
    setPage(1, 'replaceIn');
  }, [setPage, setOrderBy]);

  const onPageChanged = useCallback(
    (page: number) => {
      setPage(page, 'replaceIn');
    },
    [setPage]
  );

  const onWithdrawalPageChanged = useCallback(
    (page: number) => {
      setWithdrawalPage(page, 'replaceIn');
    },
    [setWithdrawalPage]
  );

  const onSorterChanged = ({ column, asc }: ISorter) => {
    const sortBy = `${column}|${asc ? 'ASC' : 'DESC'}`;
    if (sortBy !== orderBy) {
      setOrderBy(sortBy, 'replaceIn');
      setPage(1, 'replaceIn');
    }
  };

  const commissionTransactions: ITransaction[] = commissionTransactionsQuery.data?.records || [];
  const withdrawalTransactions: ITransaction[] = withdrawalTransactionsQuery.data?.records || [];

  const commissionTotalCount = commissionTransactionsQuery.data?.count || 0;
  const withdrawalTotalCount = withdrawalTransactionsQuery.data?.count || 0;
  const commissionPagesCount = Math.ceil(commissionTotalCount / limit) || 1;
  const withdrawalPagesCount = Math.ceil(withdrawalTotalCount / limit) || 1;

  const commissionTableFields = useMemo(
    () => [
      { key: 'id', label: _t('global.id'), sorter: false },
      { key: 'createdAt', label: _t('global.date') },
      { key: 'introducer', label: _t('commissions.introducer'), sorter: false },
      { key: 'referral', label: _t('commissions.referral'), sorter: false },
      { key: 'commissionType', label: _t('commissions.commission-type'), sorter: false },
      { key: 'actionType', label: _t('commissions.action-type'), sorter: false },
      { key: 'commissionAmount', label: _t('global.amount') },
      { key: 'showDetails', label: _t('global.details'), sorter: false },
    ],
    []
  );

  const withdrawalTableFields = useMemo(
    () => [
      { key: 'id', label: _t('global.id'), sorter: false },
      { key: 'createdAt', label: _t('global.date') },
      { key: 'customer', label: _t('global.customer'), sorter: false },
      { key: 'type', label: _t('global.type'), sorter: false },
      { key: 'status', label: _t('global.status'), sorter: false },
      { key: 'amount', label: _t('global.amount'), sorter: false },
      { key: 'showDetails', label: _t('global.details'), sorter: false },
    ],
    []
  );

  const commissionScopedSlots = useMemo(
    () => ({
      id: ({ id }: ITransaction) => <td>{shortenId(id)}</td>,
      createdAt: ({ createdAt }: ITransaction) => <td>{formatDateTime(createdAt)}</td>,
      referral: ({ affiliateCommission }: ITransaction) => (
        <td>
          {affiliateCommission?.referralName ? (
            <CLink href={`/customers/${affiliateCommission.referralId}`}>{affiliateCommission.referralName}</CLink>
          ) : (
            '-'
          )}
        </td>
      ),
      introducer: ({ customer }: ITransaction) => (
        <td>{customer?.name ? <CLink href={`/customers/${customer.id}`}>{customer.name}</CLink> : '-'}</td>
      ),
      commissionType: ({ affiliateCommission }: ITransaction) => <td>{affiliateCommission?.commissionType || '-'}</td>,
      actionType: ({ affiliateCommission }: ITransaction) => <td>{affiliateCommission?.actionType || '-'}</td>,
      commissionAmount: ({ amount, currency }: ITransaction) => <td>{formatCurrency(amount, currency)}</td>,
      showDetails: ({ id }: ITransaction) => (
        <td>
          <CButton
            color="primary"
            type="button"
            onClick={() => handleDetailsClicked(id)}
            title="View transaction details"
            size="sm"
          >
            {_t('global.details')}
          </CButton>
        </td>
      ),
    }),
    [handleDetailsClicked]
  );

  const withdrawalScopedSlots = useMemo(
    () => ({
      id: ({ id }: ITransaction) => <td>{shortenId(id)}</td>,
      customer: ({ customer }: ITransaction) => (
        <td>{customer?.name ? <CLink href={`/customers/${customer.id}`}>{customer.name}</CLink> : '-'}</td>
      ),
      amount: ({ amount, currency }: ITransaction) => <td>{formatCurrency(amount, currency)}</td>,
      status: ({ status }: ITransaction) => <td>{status}</td>,
      type: ({ type }: ITransaction) => <td>{_t(`transactionTypes.${type}`, { fallback: type })}</td>,
      createdAt: ({ createdAt }: ITransaction) => <td>{formatDateTime(createdAt)}</td>,
      showDetails: ({ id }: ITransaction) => (
        <td>
          <CButton
            color="primary"
            type="button"
            onClick={() => handleDetailsClicked(id)}
            title="View transaction details"
            size="sm"
          >
            {_t('global.details')}
          </CButton>
        </td>
      ),
    }),
    [handleDetailsClicked]
  );

  if (commissionTransactionsQuery.isError || withdrawalTransactionsQuery.isError) {
    return (
      <Error
        onRetry={() => {
          commissionTransactionsQuery.refetch();
          withdrawalTransactionsQuery.refetch();
        }}
      />
    );
  }

  const [column, isAsc] = orderBy!.split('|');
  const asc = isAsc === 'ASC';

  return (
    <PageLayout title={_t('commissions.commission-transactions')}>
      <CRow>
        <CCol>
          <CCard className="mb-4">
            <CCardHeader className="pb-0">
              <CCardTitle className="flex-grow-1">{_t('commissions.earnings')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <PaginationTable
                data={commissionTransactions}
                loading={commissionTransactionsQuery.isLoading}
                onPageChanged={onPageChanged}
                pages={commissionPagesCount}
                activePage={page ?? 1}
                tableFields={commissionTableFields}
                scopedSlots={commissionScopedSlots}
                pagination
                onSorterChanged={onSorterChanged}
                sorter={{ column, asc }}
              />
            </CCardBody>
          </CCard>

          <CCard>
            <CCardHeader className="pb-0">
              <CCardTitle className="flex-grow-1">{_t('commissions.withdrawals-and-corrections')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <PaginationTable
                data={withdrawalTransactions}
                loading={withdrawalTransactionsQuery.isLoading}
                onPageChanged={onWithdrawalPageChanged}
                pages={withdrawalPagesCount}
                activePage={withdrawalPage ?? 1}
                tableFields={withdrawalTableFields}
                scopedSlots={withdrawalScopedSlots}
                pagination
                onSorterChanged={onSorterChanged}
                sorter={{ column, asc }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default CommissionTransactionsPage;
